import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { NzImagePreviewRef, NzImageService } from 'ng-zorro-antd/image';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LotSource, LotTypeEnum } from 'src/app/modules/graphql/service/graphql-auto-main-service';
import Swiper, { SwiperOptions } from 'swiper';
import { SwiperComponent } from 'swiper/angular';

import { MediaItemIndexed } from '../../types/media-item-indexed.type';
import { MediaItem } from '../../types/media-item.type';
import { MediaOrderChanged } from '../../types/media-order-changed.type';

@Component({
  selector: 'app-lot-carousel-main-slider',
  templateUrl: './lot-carousel-main-slider.component.html',
  styleUrls: ['./lot-carousel-main-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotCarouselMainSliderComponent implements OnChanges, OnDestroy {
  @Input() lotId: string;
  @Input() lotType: LotTypeEnum;
  @Input() lotSource: LotSource;
  @Input() auctionName: string;
  @Input() activeSlideIndex = 0;
  @Input() media: MediaItem[] = [];
  @Input() isUsingSavedMedia = true;
  @Output() mediaSaved = new EventEmitter<void>();
  @Output() activeSlideIndexChange = new EventEmitter<number>();
  @Output() mediaOrderChanged = new EventEmitter<MediaItem[]>();

  @ViewChild('swiper', { static: false }) swiper!: SwiperComponent;

  configSwiper: SwiperOptions = {
    loop: true,
    keyboard: true,
    slidesPerView: 1,
    spaceBetween: 0,
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
  };

  private previewRef?: NzImagePreviewRef;

  private destroy$ = new Subject<void>();

  constructor(private imageService: NzImageService) {}

  openPreview(mediaItem: MediaItemIndexed): void {
    if (mediaItem.mediaType === 'video') {
      return;
    }

    const images = this.media.filter((item) => item.mediaType === 'image');
    this.previewRef = this.imageService.preview(
      images.map((item) => ({ src: item.source, alt: item.source })),
      { nzKeyboard: true },
    );

    this.previewRef?.switchTo(mediaItem.index);

    this.swiperRef?.keyboard?.disable();

    const previewInstance = this.previewRef.previewInstance;

    merge(previewInstance.closeClick, previewInstance.containerClick)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.swiperRef?.keyboard?.enable());
  }

  @HostListener('window:keydown.arrowLeft')
  goPrevious(): void {
    this.previewRef?.prev();
  }

  @HostListener('window:keydown.arrowRight')
  goNext(): void {
    this.previewRef?.next();
  }

  @HostListener('window:keydown.escape')
  close(): void {
    this.previewRef?.close();
    this.swiperRef?.keyboard?.enable();
  }

  setActiveSlide(index: number): void {
    this.activeSlideIndex = index;
    // this.swiperRef?.slideTo(index);
    this.activeSlideIndexChange.emit(index);
  }

  orderChanged(change: MediaOrderChanged): void {
    this.setActiveSlide(change.activeSlideIndex);
    this.mediaOrderChanged.emit(change.media);
  }

  mediaItemLoadingError(mediaItem: MediaItem): void {
    this.media = this.media.filter((mi) => mi !== mediaItem);
    this.mediaOrderChanged.emit(this.media);
  }

  trackBySource(_: number, mediaItem: MediaItemIndexed): string {
    return mediaItem.source;
  }

  private get swiperRef(): Swiper | null {
    return this.swiper?.swiperRef || null;
  }

  ngOnChanges(): void {
    if (this.swiperRef && this.swiperRef?.activeIndex !== this.activeSlideIndex) {
      this.swiperRef.slideTo(this.activeSlideIndex);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
